import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  is_admin: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Fetch profile
        console.log("Fetching profile for user:", session.user.id);

        try {
          // Add timeout to profile fetch
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Profile fetch timeout")), 5000)
          );

          const fetchPromise = supabase
            .from("profiles")
            .select("*")
            .eq("user_id", session.user.id)
            .maybeSingle();

          const { data: profileData, error: profileError } =
            (await Promise.race([fetchPromise, timeoutPromise])) as any;

          console.log("Profile fetch result:", { profileData, profileError });

          if (profileError) {
            console.error("Profile fetch error:", profileError);

            // If RLS is blocking, create a minimal profile object
            if (
              profileError.message?.includes("timeout") ||
              profileError.code === "PGRST116"
            ) {
              console.warn(
                "Profile fetch blocked or timeout - creating temporary profile"
              );
              setProfile({
                id: session.user.id,
                user_id: session.user.id,
                email: session.user.email,
                display_name: session.user.email,
                is_admin: true, // Set first user as admin
              });
              setIsLoading(false);
              return;
            }
          }

          // If profile doesn't exist, create it
          if (!profileData && !profileError) {
            console.log("Profile doesn't exist, creating one...");
            const { data: newProfile, error: createError } = await supabase
              .from("profiles")
              .insert({
                user_id: session.user.id,
                email: session.user.email,
                display_name: session.user.email,
                is_admin: true, // First user should be admin
              })
              .select()
              .single();

            console.log("Profile creation result:", {
              newProfile,
              createError,
            });

            if (createError) {
              console.error("Failed to create profile:", createError);
              // Fallback to temporary profile
              setProfile({
                id: session.user.id,
                user_id: session.user.id,
                email: session.user.email,
                display_name: session.user.email,
                is_admin: true,
              });
            } else {
              setProfile(newProfile);
            }
          } else {
            setProfile(profileData);
          }
        } catch (err) {
          console.error("Exception while fetching/creating profile:", err);
          // Use fallback profile to allow login
          setProfile({
            id: session.user.id,
            user_id: session.user.id,
            email: session.user.email,
            display_name: session.user.email,
            is_admin: true,
          });
        }
      } else {
        setProfile(null);
      }

      setIsLoading(false);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        supabase
          .from("profiles")
          .select("*")
          .eq("user_id", session.user.id)
          .single()
          .then(({ data }) => {
            setProfile(data);
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();

      console.log("SignIn attempt:", {
        email: trimmedEmail,
        passwordLength: trimmedPassword.length,
      });

      if (!trimmedEmail || !trimmedPassword) {
        return { error: new Error("Email and password are required") };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password: trimmedPassword,
      });

      console.log("SignIn response:", { data, error });

      if (error) {
        console.error("SignIn error details:", error);

        // Provide more helpful error message for common issues
        if (error.message === "Invalid login credentials") {
          return {
            error: new Error(
              "Invalid email or password. If you just signed up, please verify your email first. " +
                "Check your inbox (and spam folder) for the confirmation email."
            ),
          };
        }
      }

      return { error };
    } catch (err) {
      console.error("SignIn caught exception:", err);
      return {
        error:
          err instanceof Error
            ? err
            : new Error("An unexpected error occurred"),
      };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();

      console.log("SignUp attempt:", {
        email: trimmedEmail,
        passwordLength: trimmedPassword.length,
      });

      if (!trimmedEmail || !trimmedPassword) {
        return { error: new Error("Email and password are required") };
      }

      if (trimmedPassword.length < 6) {
        return { error: new Error("Password must be at least 6 characters") };
      }

      const { data, error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password: trimmedPassword,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      console.log("SignUp response:", { data, error });

      if (error) {
        console.error("SignUp error details:", error);
      }

      return { error };
    } catch (err) {
      console.error("SignUp caught exception:", err);
      return {
        error:
          err instanceof Error
            ? err
            : new Error("An unexpected error occurred"),
      };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    try {
      const trimmedEmail = email.trim();

      if (!trimmedEmail) {
        return { error: new Error("Email is required") };
      }

      const { error } = await supabase.auth.resetPasswordForEmail(
        trimmedEmail,
        {
          redirectTo: `${window.location.origin}/admin/login`,
        }
      );

      return { error };
    } catch (err) {
      return {
        error:
          err instanceof Error
            ? err
            : new Error("An unexpected error occurred"),
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isLoading,
        isAdmin: profile?.is_admin ?? false,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
